import React, { useState, useEffect } from 'react';
import axios from '../../utils/axios';
import toast from 'react-hot-toast';
import {
  BanknotesIcon,
  DocumentArrowDownIcon,
  CalendarIcon,
  CurrencyDollarIcon,
  BuildingOfficeIcon,
  CheckCircleIcon,
  ClockIcon,
  XCircleIcon
} from '@heroicons/react/24/outline';
import ChecksList from '../../components/agent/ChecksList';

/**
 * B2BPaymentsPage - Payment history for B2B customers
 * לקוח עסקי - היסטוריית תשלומים
 */
const B2BPaymentsPage = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPaymentDetails();
  }, []);

  const fetchPaymentDetails = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/b2b/payments');
      setData(response.data);
    } catch (error) {
      console.error('Error fetching payment details:', error);
      toast.error('שגיאה בטעינת נתוני התשלומים');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadReceipt = async (paymentId) => {
    try {
      const response = await axios.get(`/api/b2b/payments/${paymentId}/receipt/pdf`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `receipt-${paymentId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('הקבלה הורדה בהצלחה');
    } catch (error) {
      console.error('Error downloading receipt:', error);
      toast.error('שגיאה בהורדת הקבלה');
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('he-IL', {
      style: 'currency',
      currency: 'ILS'
    }).format(amount);
  };

  const getPaymentMethodLabel = (method) => {
    const labels = {
      cash: 'מזומן',
      check: 'צ\'ק',
      bank_transfer: 'העברה בנקאית'
    };
    return labels[method] || method;
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'approved':
        return (
          <span className="px-2 py-1 text-xs rounded-full bg-green-100 text-green-700 flex items-center gap-1">
            <CheckCircleIcon className="h-4 w-4" />
            אושר
          </span>
        );
      case 'pending':
        return (
          <span className="px-2 py-1 text-xs rounded-full bg-yellow-100 text-yellow-700 flex items-center gap-1">
            <ClockIcon className="h-4 w-4" />
            ממתין
          </span>
        );
      case 'rejected':
        return (
          <span className="px-2 py-1 text-xs rounded-full bg-red-100 text-red-700 flex items-center gap-1">
            <XCircleIcon className="h-4 w-4" />
            נדחה
          </span>
        );
      default:
        return <span className="px-2 py-1 text-xs rounded-full bg-gray-100 text-gray-700">{status}</span>;
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">לא נמצאו נתונים</p>
      </div>
    );
  }

  const { company, credit_summary, payments } = data;
  const isNegative = credit_summary.current_balance < 0;
  const isPositive = credit_summary.current_balance > 0;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" dir="rtl">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">תשלומים וחובות</h1>
        <p className="text-gray-600">צפייה בהיסטוריית התשלומים ומצב החוב</p>
      </div>

      {/* Company Info Card */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <div className="flex items-center gap-3 mb-4">
          <BuildingOfficeIcon className="h-8 w-8 text-primary-600" />
          <div>
            <h2 className="text-xl font-bold text-gray-900">{company.name}</h2>
            <p className="text-sm text-gray-500">{company.email}</p>
          </div>
        </div>
      </div>

      {/* Credit Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {/* Current Balance */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-medium text-gray-500">חוב נוכחי</h3>
            <CurrencyDollarIcon className={`h-5 w-5 ${
              isPositive ? 'text-red-500' :
              isNegative ? 'text-blue-500' :
              'text-green-500'
            }`} />
          </div>
          <div className={`text-2xl font-bold ${
            isPositive ? 'text-red-600' :
            isNegative ? 'text-blue-600' :
            'text-green-600'
          }`}>
            {isNegative ? '-' : ''}{formatCurrency(Math.abs(credit_summary.current_balance))}
          </div>
          <p className="text-xs text-gray-500 mt-1">
            {isNegative ? 'יתרה חיובית (שילמת מראש)' : 'חוב נוכחי'}
          </p>
        </div>

        {/* Credit Limit */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-medium text-gray-500">מגבלת אשראי</h3>
            <BanknotesIcon className="h-5 w-5 text-gray-400" />
          </div>
          <div className="text-2xl font-bold text-gray-900">
            {formatCurrency(credit_summary.credit_limit)}
          </div>
          <p className="text-xs text-gray-500 mt-1">מגבלה מאושרת</p>
        </div>

        {/* Used Credit */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-medium text-gray-500">אשראי בשימוש</h3>
            <CurrencyDollarIcon className="h-5 w-5 text-orange-500" />
          </div>
          <div className="text-2xl font-bold text-orange-600">
            {formatCurrency(credit_summary.used_credit)}
          </div>
          <p className="text-xs text-gray-500 mt-1">סך אשראי בשימוש</p>
        </div>

        {/* Available Credit */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-medium text-gray-500">אשראי זמין</h3>
            <CheckCircleIcon className="h-5 w-5 text-green-500" />
          </div>
          <div className="text-2xl font-bold text-green-600">
            {formatCurrency(credit_summary.available_credit)}
          </div>
          <p className="text-xs text-gray-500 mt-1">נותר לך</p>
        </div>
      </div>

      {/* Payments Table */}
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">היסטוריית תשלומים</h2>
        </div>

        {payments.length === 0 ? (
          <div className="text-center py-12">
            <BanknotesIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500">אין תשלומים להצגה</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    מספר קבלה
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    תאריך
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    סכום
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    אמצעי תשלום
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    נגבה על ידי
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    סטטוס
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    פעולות
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {payments.map((payment) => (
                  <React.Fragment key={payment.id}>
                    <tr className="hover:bg-gray-50">
                      <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {payment.receipt_number}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                        <div className="flex items-center gap-2">
                          <CalendarIcon className="h-4 w-4 text-gray-400" />
                          {new Date(payment.payment_date).toLocaleDateString('he-IL', {
                            day: '2-digit',
                            month: '2-digit',
                            year: 'numeric'
                          })}
                        </div>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm font-bold text-green-600">
                        {formatCurrency(payment.amount)}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                        <div className="flex flex-col gap-1">
                          <span>{getPaymentMethodLabel(payment.payment_method)}</span>
                          {payment.checks?.length > 0 && (
                            <span className="px-2 py-0.5 text-xs rounded-full bg-blue-100 text-blue-700">
                              {payment.checks.length} צ'קים
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                        {payment.collector ? payment.collector.name : 'לא זמין'}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        {getStatusBadge(payment.status)}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm font-medium">
                        <button
                          onClick={() => handleDownloadReceipt(payment.id)}
                          className="text-primary-600 hover:text-primary-800 flex items-center gap-1"
                        >
                          <DocumentArrowDownIcon className="h-4 w-4" />
                          הורד
                        </button>
                      </td>
                    </tr>
                    {payment.checks?.length > 0 && (
                      <tr>
                        <td colSpan="7" className="px-4 py-2">
                          <ChecksList payment={payment} />
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default B2BPaymentsPage;

